import { Tab, Tabs, Typography } from '@material-ui/core';
import { useEffect } from 'react';
import { auth, CoderNote } from '../../core';
import { documentResolver } from '../../core/document-resolver';
import { codingTabIndex } from '../../core/hospitalist-context';
import { HBox } from '../../core/layout';
import { RequestActivity, Spinner } from '../../core/loading';
import { PageDrawer } from '../../core/page';
import { lightGrayBorder, mediumGray } from '../../core/theme';
import { toggleButtonApi } from '../../core/toggle-button';
import { VisitContext, WorksheetApis, navigateToList, visitContextApi } from '../../core/visit-context';
import { isReviewWorkflow, workflows } from '../../core/workflow-api';
import { Summary } from '../common/audit-summary';
import { CodeReference, CodingListLink, DocumentsLink, LogoutLink, ReportBug } from '../common/navigation';

import { TabPanel } from '../common/tabs';

import { VisitDrawer, visitDrawerTabs } from './coding-components';
import { HospitalistCodingActionsDialogue } from './hospitalist-coding-actions';

import { WorksheetSelectionMenu } from './related-worksheets';
import { WorksheetTab } from './worksheet';
import { NoteSummary } from './note-summary';
import { NotesWrapper } from './coding-route';

function HospitalistCodingTabs(props?: VisitContext) {
  let { worksheets, worksheetTypes, hasAuditSummary, notesApi, hospitalist, visit, hospitalistContext, workflowNames, onTransitionDialogueComplete, getApiById, alertsV2 } = props;
  visitContextApi((s) => s.context);
  worksheets = visitContextApi((s) => s.worksheets) || worksheets;
  const index = codingTabIndex((s) => s.index);

  const onChange = (_: any, i: number) => {
    codingTabIndex.setState({ index: i });
  };

  workflows.data((s) => s.feature);

  const w = workflows.data((s) => s.workflow);
  const flow = w?.prompt || w?.name || '';

  let selectedApi: WorksheetApis = hospitalistContext.getWorksheet();

  const worksheetCtx = selectedApi.context;
  const selected = worksheetCtx.selection.data((s) => s.selected);
  const selectedWorksheetId = selected?.worksheetId || worksheetCtx.worksheet.worksheetId;
  selectedApi = getApiById(selectedWorksheetId);

  props.data((s) => s.contextChanged);

  const onVisitClick = () => {
    const transition = props.getOpenTransition();
    transition.setState({
      openTransitionDialogue: true,
      transitionDialogueComplete: () => {
        if (!hospitalist) {
          navigateToList(visit);
        } else {
          if (onTransitionDialogueComplete) {
            onTransitionDialogueComplete(transition.getState().worksheetState);
          }
        }
      },
    });
    props.refreshRouteApis();
  };

  const auditReviewEnabled = hasAuditSummary || isReviewWorkflow();


  notesApi.apis = worksheets;

  useEffect(() => {
    if (props.readonly) {
      setTimeout(() => codingTabIndex.setState({ index: 0 }), 20);
    }
  }, []);

  const onOpenDocumentWindow = () => {
    visit.worksheetId = selectedWorksheetId;
    documentResolver.openWindow(visit);
  };

  return (
    worksheets?.length > 0 && (
      <>
        <RequestActivity />
        <div style={{ width: '100%', maxWidth: '100vw' }}>
          <Tabs style={{ paddingLeft: '46px', paddingTop: '0px', marginTop: 0 }} value={index} onChange={onChange}>
            {hospitalist
              ? worksheets.map((v) => (
                  <Tab
                    key={v.context.worksheet.worksheetId}
                    label={<WorksheetSelectionMenu api={v.context.selection} hospitalist={hospitalistContext} visit={visit} worksheet={v.context.worksheet} />}
                  />
                ))
              : worksheetTypes.map((v, i) => <Tab key={v} label={v + ' - ' + worksheets[i].context.worksheet.departmentCode} />)}

            {auditReviewEnabled && <Tab label="Audit Summary "></Tab>}
          </Tabs>
          {hospitalist
            ? worksheets.map((v, i) => (
                <TabPanel key={v.context.worksheet.worksheetId} value={index} index={i}>
                  <WorksheetTab {...v} hospitalistContext={hospitalistContext} alertsV2={alertsV2} />
                </TabPanel>
              ))
            : worksheetTypes.map((w, i) => (
                <TabPanel key={w} value={index} index={i}>
                  <WorksheetTab {...worksheets[i]} alertsV2={alertsV2}  />
                </TabPanel>
              ))}

          {auditReviewEnabled && (
            <TabPanel value={index} index={worksheets.length}>
              <Summary apis={notesApi.apis} workflows={workflowNames} />
            </TabPanel>
          )}
        </div>
        <div
          style={{
            position: 'absolute',
            right: '12px',
            top: '0px',
            zIndex: 10,
          }}
        >
          <HBox spacing={0}>
            <Typography variant="h6" style={{ color: mediumGray }}>
              {`${flow} ${props.readonly ? ' (Read Only)' : ''}`}
            </Typography>
            <DocumentsLink onClick={onOpenDocumentWindow} htmlColor={mediumGray} />
            <CodeReference htmlColor={mediumGray} api={selectedApi.icdCpt} />
            {!props.readonly && <CodingListLink onClick={onVisitClick} htmlColor={mediumGray} />}
            <ReportBug htmlColor={mediumGray} />
            <LogoutLink htmlColor={mediumGray} onClick={() => auth.signOut(true)} />
          </HBox>
        </div>
        <HospitalistCodingActionsDialogue context={props} />
      </>
    )
  );
}

const toggleSideBar = toggleButtonApi();
export const codingDrawerWidth = 362;

const backgroundStyle: any = {
  position: 'absolute',
  left: '0px',
  top: '0px',
  width: '100%',
  height: '48px',
  zIndex: -1,
  borderBottom: `solid 1px ${lightGrayBorder}`,
  backgroundColor: 'white',
};

export function HospitalistCodingTool() {
  const ctx = visitContextApi((s) => s.context);
  useEffect(() => {
    visitDrawerTabs.setState({ index: 0 });
  }, []);
  return (
    <>
      <div style={backgroundStyle} />
      <PageDrawer
        main={HospitalistCodingTabs(ctx)}
        drawerHeader={'Visit - ' + ctx.visit.locationCode}
        drawerWidth={codingDrawerWidth}
        drawerOpen={true}
        drawer={VisitDrawer(ctx)}
        toggle={toggleSideBar}
      />
    </>
  );
}

function VisitContextLoader({ children }) {
  const ctx = visitContextApi((s) => s.context);
  const loading = visitContextApi((s) => s.loading);
  toggleSideBar.setState({ active: true });
  return <Spinner complete={!loading && ctx}>{children}</Spinner>;
}

type HospitalistCodingRouteProps = {
  ctx: VisitContext,
};

export function HospitalistCodingRoute(props?: HospitalistCodingRouteProps) {
  visitDrawerTabs.setState({ index: 0 });
  const {ctx} = props;
  return (
    <VisitContextLoader>
      {ctx && <NotesWrapper ctx={ctx} />}
      <HospitalistCodingTool />
    </VisitContextLoader>
  );
}
